import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '@/hooks/use-local-storage';
import type { FlowType, ListServicesParams } from '@/types/service';

/**
 * Spec 352 §2: URL-as-source-of-truth filter state for /services.
 *
 * The hook is the SINGLE adapter between the short URL deep-link names
 * (`merchant`/`gw`/`flow`/`iqServiceId` — shared byte-for-byte with the #347
 * counter links) and the API-long `ListServicesParams` keys
 * (`merchantId`/`gatewayServiceId`/`flowType`/`iqServiceId`). It returns `filters`
 * already API-shaped so `useServices({ ...filters, cursor })` drops in directly and
 * no caller passes URL-shaped keys into the query (Codex #3).
 *
 * Filter params PERSIST in the URL (shareable / back-button-safe) — unlike the
 * #347 `?key=` one-shot deep-link, they do NOT strip themselves. The blaetter
 * cursor lives locally on the page (cursorStack), never in the URL.
 *
 * Spec 380: zusaetzlich „zuletzt genutzt" via localStorage-Snapshot (short names). Die
 * URL bleibt Source-of-Truth; aus dem Snapshot wird nur vorbelegt, wenn die URL keine
 * Filter traegt (Deep-Links gewinnen). Der Snapshot spiegelt jede URL-Aenderung (Setter
 * ODER extern), leeren Zustand eingeschlossen — kein stale Reseed.
 */

const SERVICE_FILTERS_KEY = 'services.filters.v1';

// URL deep-link param names (short). The View toggle lives in localStorage, not here.
const FILTER_KEYS = ['merchant', 'gw', 'flow', 'iqServiceId'] as const;

/** Snapshot in URL-Kurzform (alle Werte Strings). */
type ServiceFilterSnapshot = { [K in (typeof FILTER_KEYS)[number]]?: string };

/** The API-shaped filter subset, ready to spread into useServices / listServices. */
export type ServiceFilters = Pick<
  ListServicesParams,
  'merchantId' | 'gatewayServiceId' | 'flowType' | 'iqServiceId'
>;

function isServiceFilterSnapshot(v: unknown): v is ServiceFilterSnapshot {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Aktuellen URL-Filterzustand als Snapshot abbilden (nur vorhandene Kurz-Keys). */
function serviceSnapshotFromParams(params: URLSearchParams): ServiceFilterSnapshot {
  const snap: ServiceFilterSnapshot = {};
  for (const key of FILTER_KEYS) {
    const v = params.get(key);
    if (v !== null && v !== '') snap[key] = v;
  }
  return snap;
}

/**
 * Snapshot → URLSearchParams mit Feld-Sanitisierung (§4.4): `flow` nur aus dem erlaubten
 * Set; merchant/gw/iqServiceId sind freie String-IDs (nur Vorhandensein). Eine veraltete
 * ID liefert eine leere Liste (Empty-State), keinen Crash — wie ein alter geteilter Link.
 */
function buildServiceSeedParams(s: ServiceFilterSnapshot): URLSearchParams {
  const p = new URLSearchParams();
  if (typeof s.merchant === 'string' && s.merchant) p.set('merchant', s.merchant);
  if (typeof s.gw === 'string' && s.gw) p.set('gw', s.gw);
  if (s.flow === 'WEB_TAN' || s.flow === 'WAP_CLICK') p.set('flow', s.flow);
  if (typeof s.iqServiceId === 'string' && s.iqServiceId) p.set('iqServiceId', s.iqServiceId);
  return p;
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | undefined | null,
): void {
  if (value == null || value === '') params.delete(key);
  else params.set(key, value);
}

export interface UseServiceFilterParams {
  /** API-shaped filters — spread directly into useServices({ ...filters, cursor }). */
  filters: ServiceFilters;
  /** True as soon as any of the four facet params is set. */
  hasActiveFilters: boolean;
  setMerchant: (value: string | undefined) => void;
  setGw: (value: string | undefined) => void;
  setFlow: (value: string | undefined) => void;
  setIqServiceId: (value: string | undefined) => void;
  /** Clear every filter → bare `/services`. */
  reset: () => void;
}

export function useServiceFilterParams(): UseServiceFilterParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useLocalStorage<ServiceFilterSnapshot>(
    SERVICE_FILTERS_KEY,
    {},
    isServiceFilterSnapshot,
  );

  const filters = useMemo<ServiceFilters>(() => {
    const merchant = searchParams.get('merchant') ?? undefined;
    const gw = searchParams.get('gw') ?? undefined;
    const flow = (searchParams.get('flow') as FlowType | null) ?? undefined;
    const iqServiceId = searchParams.get('iqServiceId') ?? undefined;
    return {
      ...(merchant ? { merchantId: merchant } : {}),
      ...(gw ? { gatewayServiceId: gw } : {}),
      ...(flow ? { flowType: flow } : {}),
      ...(iqServiceId ? { iqServiceId } : {}),
    };
  }, [searchParams]);

  const hasActiveFilters = FILTER_KEYS.some((key) => searchParams.has(key));

  // Spec 380: zwei Refs schuetzen die zwei bekannten Effekt-Fallen (s. Header).
  const seededRef = useRef(false);
  const skipNextMirrorRef = useRef(false);

  // Seed-on-mount: leere URL + nicht-leerer (sanitisierter) Snapshot → URL vorbelegen.
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (hasActiveFilters) return;
    const seeded = buildServiceSeedParams(snapshot);
    if (Array.from(seeded.keys()).length > 0) {
      skipNextMirrorRef.current = true;
      setSearchParams(seeded, { replace: true });
    }
  }, [hasActiveFilters, snapshot, setSearchParams]);

  // Spiegeln: jede URL-Aenderung (Setter ODER extern) in den Snapshot, leer eingeschlossen.
  useEffect(() => {
    if (!seededRef.current) return;
    if (skipNextMirrorRef.current) {
      skipNextMirrorRef.current = false;
      return;
    }
    setSnapshot(serviceSnapshotFromParams(searchParams));
  }, [searchParams, setSnapshot]);

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setMerchant = useCallback(
    (value: string | undefined) => update((p) => setOrDelete(p, 'merchant', value)),
    [update],
  );
  const setGw = useCallback(
    (value: string | undefined) => update((p) => setOrDelete(p, 'gw', value)),
    [update],
  );
  const setFlow = useCallback(
    (value: string | undefined) => update((p) => setOrDelete(p, 'flow', value)),
    [update],
  );
  const setIqServiceId = useCallback(
    (value: string | undefined) => update((p) => setOrDelete(p, 'iqServiceId', value)),
    [update],
  );

  // Spec 380 §4.3: Reset leert URL UND Snapshot.
  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setSnapshot({});
  }, [setSearchParams, setSnapshot]);

  return {
    filters,
    hasActiveFilters,
    setMerchant,
    setGw,
    setFlow,
    setIqServiceId,
    reset,
  };
}
